import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login/Login';
import Dashboard from './pages/Dashboard/Dashboard';
import Loads from './pages/Loads/Loads';
import Users from './pages/Users/Users';
import UserDetail from './pages/UserDetail/UserDetail';
import CreateUser from './pages/CreateUser/CreateUser';
import Clients from './pages/Clients/Clients';
import ClientDetail from './pages/ClientDetail/ClientDetail';
import Deliveries from './pages/Deliveries/Deliveries';
import DeliveryForm from './pages/DeliveryForm/DeliveryForm';
import RequirePermission from './components/RequirePermission';
import ErrorBoundary from './components/ErrorBoundary';
import AppLayout from './layout/AppLayout';
import RequireAuth from './components/RequireAuth';
import Skeleton from './components/ui/Skeleton/Skeleton';
import { FEATURE_TRACKING, FEATURE_MULTIMODAL, LAZY_ROUTES } from './featureFlags';
import { MODULE_PERMISSIONS, ModulePath } from './lib/modulePermissions';

/* -------------------------------------------------------------------------- */
/*                        Carga diferida de pantallas                         */
/* -------------------------------------------------------------------------- */

/**
 * Envoltura que decide entre carga diferida y carga directa.
 *
 * ## Qué resuelve
 *
 * El bundle principal incluía **todas** las pantallas, así que abrir el login descargaba
 * también el detalle de carga, Mapbox y el generador de pre-alerta. Con `React.lazy` cada
 * pantalla llega en su propio archivo la primera vez que se visita.
 *
 * ## 🔑 El retorno fácil, que es lo que se pidió
 *
 * Con `REACT_APP_EAGER_ROUTES=true` **se dispara la descarga de todas las pantallas al
 * arrancar**, así que ninguna navegación espera: es el comportamiento anterior desde el punto
 * de vista de quien usa la aplicación. No hay que revertir código ni tocar este archivo, se
 * compara lado a lado cambiando una variable.
 *
 * 📌 Por qué se resuelve **precargando** y no volviendo a un `import` estático: un `import`
 * estático y uno dinámico no pueden convivir para el mismo módulo sin duplicarlo en el
 * bundle. Precargar deja una sola forma de declarar las rutas —así las dos ramas no se
 * separan— y consigue lo que importa: que nadie espere al navegar.
 *
 * ⚠️ El `lazy()` va **fuera** del componente, en el módulo. Llamarlo dentro del render
 * crearía un componente nuevo en cada pintada y React desmontaría y volvería a montar la
 * pantalla: se perdería el estado y volvería a pedir los datos en cada cambio de estado del
 * padre.
 */
const load = <T extends { default: React.ComponentType<any> }>(
  importer: () => Promise<T>
): React.LazyExoticComponent<React.ComponentType<any>> => {
  const Screen = React.lazy(importer);

  if (!LAZY_ROUTES) {
    /**
     * Se pide el archivo de inmediato, sin esperar a que alguien navegue.
     *
     * El `catch` vacío no esconde un problema: si la descarga falla, `Suspense` la volverá a
     * intentar cuando la pantalla se renderice de verdad, y ahí sí el error llega a la
     * interfaz. Sin el `catch`, un fallo de red al arrancar dejaría una promesa rechazada sin
     * manejar en la consola de todos los usuarios.
     */
    void importer().catch(() => undefined);
  }

  return Screen;
};

/**
 * Pantalla mientras llega el archivo de una ruta **sin shell** (login y las públicas).
 *
 * 🔑 Las pantallas del área autenticada **ya no pasan por aquí**: su `Suspense` vive dentro de
 * `AppLayout`, alrededor del contenido, así que al cambiar de módulo el sidebar y el fondo se quedan.
 * Antes este respaldo sustituía el árbol completo y producía un pantallazo blanco entre módulos.
 *
 * ⚠️ Este sí lleva **fondo y alto de pantalla**. Sin ellos se veía el blanco del documento, que no es
 * el color de ningún tema — con tema oscuro el golpe era peor. Es el mismo defecto que se acaba de
 * corregir, y aquí seguiría ocurriendo al abrir un enlace público en frío.
 *
 * Se usan bloques y no un spinner centrado: el archivo suele llegar en decenas de milisegundos, y un
 * spinner que aparece y desaparece tan rápido se lee como un parpadeo.
 */
const RouteFallback: React.FC = () => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 16,
      padding: 24,
      minHeight: '100vh',
      boxSizing: 'border-box',
      background: 'var(--dylo-page-bg)',
    }}
  >
    <Skeleton width={220} height={28} radius={10} />
    <Skeleton height={120} radius={16} />
    <Skeleton height={320} radius={16} />
  </div>
);

/**
 * Pantallas diferidas. **Login, Dashboard y Loads se quedan en el bundle principal** a
 * propósito: son la entrada y las dos pantallas que más se abren, así que diferirlas
 * cambiaría un parpadeo por otro sin ganar nada — el usuario las va a pedir de todas formas
 * en los primeros segundos.
 */
const GuideDetail = load(() => import('./pages/GuideDetail/GuideDetail'));
const CreateShipment = load(() => import('./pages/CreateShipment/CreateShipment'));
const TrackShipment = load(() => import('./pages/TrackShipment/TrackShipment'));
const Ocean = load(() => import('./pages/Ocean/Ocean'));
const Air = load(() => import('./pages/Air/Air'));
const GlobalView = load(() => import('./pages/GlobalView/GlobalView'));
const ShipmentDetail = load(() => import('./pages/ShipmentDetail/ShipmentDetail'));
const GlobalDashboard = load(() => import('./pages/GlobalDashboard/GlobalDashboard'));
const PublicSearch = load(() => import('./pages/Public/PublicSearch'));
const PublicShipmentDetail = load(() => import('./pages/Public/PublicShipmentDetail'));
const PublicDetail = load(() => import('./pages/Public/PublicDetail'));
const PreAlert = load(() => import('./pages/PreAlert/PreAlert'));
const PublicPreAlert = load(() => import('./pages/Public/PublicPreAlert'));
const Audit = load(() => import('./pages/Audit/Audit'));
const Catalogs = load(() => import('./pages/Catalogs/Catalogs'));
/**
 * MI PERFIL. Diferida porque se abre poco —una persona cambia su contraseña dos veces al año— y no
 * tiene sentido que su código viaje en el paquete que descarga todo el mundo al entrar.
 */
const Profile = load(() => import('./pages/Profile/Profile'));
/**
 * BANDEJA DE NOTIFICACIONES. Diferida: la campanita del sidebar ya cubre el vistazo rápido, así que esta
 * pantalla se abre cuando alguien quiere buscar en el historial — no en cada sesión.
 */
const Notifications = load(() => import('./pages/Notifications/Notifications'));
/**
 * NOVEDADES. Diferida como el resto: se abre cuando hay algo nuevo, no en cada sesión.
 *
 * 📌 **Sin permiso y sin bandera de función**, igual que la bandeja y por un motivo extra: el contenido es el mismo
 * para todos y ya está desplegado, así que un permiso aquí sería una puerta que no protege nada — y crearía el caso
 * absurdo de un usuario al que no se le puede informar de los cambios de la plataforma que usa.
 */
const WhatsNew = load(() => import('./pages/WhatsNew/WhatsNew'));
const VerifyEmail = load(() => import('./pages/Public/VerifyEmail'));
const SubscribeUpdates = load(() => import('./pages/Public/SubscribeUpdates'));
const UnsubscribeUpdates = load(() => import('./pages/Public/UnsubscribeUpdates'));

/**
 * La pantalla de un módulo del menú, con los mismos permisos con los que el menú la ofrece (19-oct-2026).
 *
 * 🔑 Si el menú no muestra *Users*, escribir `/users` en la barra tampoco la abre: lleva al dashboard, como hacía ya
 * `/audit`. Los permisos de cada módulo viven en `lib/modulePermissions.ts`, los mismos que pide el backend.
 *
 * 📌 Solo las entradas del menú. El detalle y el alta (`/users/:id`, `/clients/:id`, `/deliveries/new`…) no se envuelven:
 * se llega a ellos desde enlaces que ya revisan su propio permiso, y algunos piden uno distinto al de la lista (el detalle
 * de un cliente pide `CL_VIEW`, no `CLIENTS`). Envolverlos con el del módulo cerraría caminos que hoy funcionan.
 *
 * ⚠️ `/` y `/dashboard` tampoco: son la entrada, y `RequirePermission` manda ahí a quien no cumple. Envolverlas
 * produciría un rebote sin fin para quien no tuviera `LOADS`.
 */
const moduleScreen = (path: ModulePath, screen: React.ReactNode) => (
  <RequirePermission permissions={[...MODULE_PERMISSIONS[path]]}>{screen}</RequirePermission>
);

const AppV2Routes: React.FC = () => {
  /**
   * 📌 **Un solo `Suspense` alrededor de todas las rutas**, no uno por ruta.
   *
   * Solo hay una pantalla montada a la vez, así que un `Suspense` por ruta serían quince
   * envolturas para el mismo efecto. Y con `REACT_APP_EAGER_ROUTES=true` no estorba: si
   * ningún hijo suspende, `Suspense` no hace nada.
   */
  const routes = (
    <Routes>
      <Route path="/login" element={<Login />} />

      {/* Públicas (sin autenticación ni layout) */}
      <Route path="/find-load" element={<PublicSearch />} />
      <Route path="/public-load/:id" element={<PublicDetail />} />
      {/**
       * Detalle público de embarque marítimo/aéreo. Una sola ruta para las dos
       * modalidades: el backend resuelve en qué tabla vive el id, igual que hace
       * `/shipments/:id` del lado interno. Si la ruta llevara la modalidad, el
       * buscador tendría que saberla antes de navegar.
       */}
      {/**
       * 🔄 **Detrás de `FEATURE_MULTIMODAL` desde el 14-oct-2026**, como el resto de esos módulos: mientras solo se
       * publica terrestre, un enlace público a un marítimo o un aéreo no abre nada. El buscador público tampoco los
       * ofrece (`PublicSearch`).
       */}
      {FEATURE_MULTIMODAL && (
        <Route path="/public-shipment/:id" element={<PublicShipmentDetail />} />
      )}
      {/**
       * VERIFICACIÓN DE CORREO. Es la pantalla a la que apunta el enlace del correo.
       *
       * 🔑 Va con las públicas —sin sesión y sin shell— porque el usuario la abre desde su bandeja,
       * probablemente en el teléfono. Pedirle entrar antes volvería el trámite imposible justo para quien
       * más lo necesita, y no haría falta: el token **es** la credencial.
       *
       * ⚠️ **Sin `FEATURE_MULTIMODAL`.** Este enlace lo emite "Mi perfil", que está encendido para todos.
       * Si la ruta dependiera del flag, un correo ya mandado dejaría de funcionar al apagarlo — y el
       * usuario vería "esta página no existe" sin ninguna explicación posible. (`/pre-alert/:token`
       * también salió del flag, el 28-oct-2026.)
       */}
      <Route path="/verify-email/:token" element={<VerifyEmail />} />

      {/**
       * ALTA Y BAJA DE LAS ACTUALIZACIONES AL CLIENTE.
       *
       * 🔴 **Van entre las públicas y es imprescindible, no una comodidad.** Quien abre estos enlaces es
       * alguien del cliente: **no tiene cuenta y no puede tenerla**. Si estas rutas quedaran detrás del
       * login, la baja exigiría llamarnos por teléfono — que es exactamente lo que se pidió evitar.
       *
       * 🔑 Y `/unsubscribe` **no da de baja al abrir**: consulta y muestra un botón. Los antivirus de correo
       * y el prefetch de Gmail abren los enlaces solos, así que la baja por apertura daría de baja a los
       * clientes en silencio y el síntoma aparecería semanas después. Ver la nota del componente.
       */}
      <Route path="/subscribe/:token" element={<SubscribeUpdates />} />
      <Route path="/unsubscribe/:token" element={<UnsubscribeUpdates />} />
      {/**
       * PRE-ALERTA por enlace compartido. Va con el resto de las públicas: sin
       * autenticación y sin shell, porque la usa gente de fuera.
       *
       * 🔄 **Fuera de `FEATURE_MULTIMODAL` desde el 28-oct-2026**, como el generador interno (abajo): la pre-alerta se
       * publica antes que aéreo y marítimo. Los enlaces ya emitidos sirven desde ese día (el token vive en la base, no
       * en la ruta), y el API ya los atendía: `/public/pre-alert/:token` nunca dependió de su bandera.
       */}
      <Route path="/pre-alert/:token" element={<PublicPreAlert />} />

      {/* Área autenticada: shell con sidebar + header + footer */}
      <Route
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      >
        <Route path="/" element={<Dashboard />} />
        <Route path="/dashboard" element={<Dashboard />} />
        {/* 🔄 19-oct-2026: las entradas del menú, con el permiso con el que el menú las ofrece (ver `moduleScreen`). */}
        <Route path="/loads" element={moduleScreen('/loads', <Loads />)} />
        <Route path="/guide/detail/:id" element={<GuideDetail />} />
        <Route path="/new-load" element={moduleScreen('/new-load', <CreateShipment />)} />
        <Route path="/users" element={moduleScreen('/users', <Users />)} />
        <Route path="/users/new" element={<CreateUser />} />
        <Route path="/users/:id" element={<UserDetail />} />
        <Route path="/clients" element={moduleScreen('/clients', <Clients />)} />
        <Route path="/clients/:id" element={<ClientDetail />} />
        <Route path="/deliveries" element={moduleScreen('/deliveries', <Deliveries />)} />
        <Route path="/deliveries/new" element={<DeliveryForm />} />
        <Route path="/deliveries/:id" element={<DeliveryForm />} />
        <Route path="/catalogs" element={moduleScreen('/catalogs', <Catalogs />)} />
        {/**
         * Generador de PRE-ALERTA aérea. La versión pública con token está arriba, entre las rutas sin autenticación.
         *
         * 🔄 **Fuera del bloque de `FEATURE_MULTIMODAL` desde el 28-oct-2026** (*"habilitar la pre alerta para produccion
         * sin habilitar todavia el resto de aereo y maritimo"*): la abre quien tenga `PA_VIEW` (`MODULE_PERMISSIONS`).
         */}
        <Route path="/pre-alert" element={moduleScreen('/pre-alert', <PreAlert />)} />
        {/* Módulos en desarrollo: las rutas solo existen con el feature flag
            activo. Sin él, el catch-all de abajo redirige al dashboard. */}
        {FEATURE_TRACKING && <Route path="/tracking" element={moduleScreen('/tracking', <TrackShipment />)} />}

        {FEATURE_MULTIMODAL && (
          <>
            <Route path="/global-view" element={moduleScreen('/global-view', <GlobalView />)} />
            <Route path="/ocean" element={moduleScreen('/ocean', <Ocean />)} />
            <Route path="/air" element={moduleScreen('/air', <Air />)} />
            {/* Detalle compartido por marítimo y aéreo */}
            <Route path="/shipments/:id" element={<ShipmentDetail />} />
            <Route path="/global-dashboard" element={moduleScreen('/global-dashboard', <GlobalDashboard />)} />
          </>
        )}
        {/**
         * Bitácora de actividad. Vive dentro de la sección **Developer**, así que
         * exige los dos permisos igual que el backend: `DEV_ACCESS` abre la sección
         * y `AU_VIEW` habilita la bitácora dentro.
         *
         * 📌 Mira el **permiso**, no el interruptor de "Developer view". Es
         * deliberado: el interruptor es una preferencia de vista para no tener el
         * bloque verde compitiendo por la atención, no una revocación de acceso. Si
         * estuviera atado a él, un marcador guardado a `/audit` dejaría de funcionar
         * al esconder la sección, que no es lo que el interruptor promete.
         *
         * `RequirePermission` solo evita aterrizar en una pantalla que va a
         * responder 403 en cada petición; quien niega de verdad es el backend.
         *
         * 🔄 **Fuera del bloque de `FEATURE_MULTIMODAL` desde el 14-oct-2026.** Developer no es un módulo multimodal:
         * con el flag apagado, la sección del menú llevaba a una ruta que no existía, y *Ready to deploy* es justo lo
         * que se mira el día de subir.
         */}
        {/* 📌 Los mismos `DEV_ACCESS` + `AU_VIEW` de siempre, ahora desde `MODULE_PERMISSIONS` como el resto del menú. */}
        <Route path="/audit" element={moduleScreen('/audit', <Audit />)} />
        {/**
         * MI PERFIL.
         *
         * 🔑 **Sin `RequirePermission`, y es deliberado.** Todo el que puede entrar tiene que poder
         * cambiar su contraseña; un permiso aquí solo lograría que a alguien se le olvide marcárselo y
         * quede sin poder hacerlo. El backend tampoco lo pide: el control no es "qué permiso tienes"
         * sino "sobre quién actúas", y ninguna ruta de `/me` acepta apuntar a otra persona.
         *
         * 📌 La ruta se llama `/account` porque es la que ya usaba el menú de la cuenta. Renombrarla a
         * `/profile` rompería los marcadores que alguien tenga guardados por nada.
         */}
        <Route path="/account" element={<Profile />} />
        {/**
         * BANDEJA DE NOTIFICACIONES.
         *
         * 🔑 **Sin `RequirePermission` ni feature flag**, por lo mismo que `/account`: es la bandeja de uno
         * mismo, y todo el que entra tiene una. Un permiso aquí solo lograría que a alguien se le olvide
         * marcárselo y quede sin poder leer sus propios avisos; un flag dejaría sin destino a los enlaces de
         * la campanita, que sí está siempre encendida.
         *
         * 📌 Y **no va detrás de `FEATURE_MULTIMODAL`** aunque casi todos los avisos vengan de marítimo y
         * aéreo: los de terrestre no, y esos existen con el flag apagado.
         */}
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/whats-new" element={<WhatsNew />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );

  /**
   * 🔑 **Un segundo boundary, para lo que vive FUERA del layout.**
   *
   * El del layout cubre las pantallas del área autenticada dejando el sidebar en pie. Pero el login, la búsqueda
   * pública, el detalle público, la verificación de correo y las altas y bajas del cliente **no pasan por el
   * layout**: si una de ésas falla, sin este boundary el usuario vuelve a la pantalla en blanco.
   *
   * 🔴 Y son justo las que más importan, porque las abre **gente de fuera** —un cliente pulsando el enlace de baja
   * desde su correo— que no tiene a quién preguntarle ni sabe que existe un sidebar.
   */
  return (
    <ErrorBoundary label="routes.root">
      <React.Suspense fallback={<RouteFallback />}>{routes}</React.Suspense>
    </ErrorBoundary>
  );
};

export default AppV2Routes;
